import { Controller } from "@hotwired/stimulus"
import Rails from "@rails/ujs"
import { Toast } from "bootstrap"
import L from "leaflet"

export default class extends Controller {
  static targets = ["map", "geojson", "warehouse", "status", "counter", "submit"]
  static values = { defaultLatitude: Number, defaultLongitude: Number }

  connect() {
    this.points = this.readPoints()
    this.markers = []
    this.map = L.map(this.mapTarget, { zoomControl: true }).setView(this.initialCenter(), 12)

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: "&copy; OpenStreetMap"
    }).addTo(this.map)

    this.polygon = L.polygon([], {
      color: "#0d6efd",
      fillColor: "#0d6efd",
      fillOpacity: 0.14,
      weight: 3
    }).addTo(this.map)

    this.map.on("click", (event) => this.addPoint(event.latlng))
    this.element.addEventListener("submit", this.validateBeforeSubmit)
    this.render()

    if (this.points.length >= 3) {
      this.map.fitBounds(L.latLngBounds(this.points), { padding: [24, 24] })
    } else {
      this.centerOnWarehouse()
    }
  }

  disconnect() {
    this.element.removeEventListener("submit", this.validateBeforeSubmit)
    this.map?.remove()
  }

  validateBeforeSubmit = (event) => {
    if (this.points.length >= 3) return

    event.preventDefault()
    const message = "Desenhe uma região com pelo menos três pontos no mapa."
    this.statusTarget.textContent = message
    this.statusTarget.classList.add("text-danger")
    this.mapTarget.classList.add("border", "border-danger")
    this.showValidationToast(message)
    this.mapTarget.scrollIntoView({ behavior: "smooth", block: "center" })
    setTimeout(() => Rails.enableElement(this.element), 25)
  }

  centerOnWarehouse() {
    const coordinates = this.selectedWarehouseCoordinates() || this.defaultWarehouseCoordinates()
    if (coordinates) this.map.setView(coordinates, 13)
  }

  initialCenter() {
    return this.selectedWarehouseCoordinates() || this.defaultWarehouseCoordinates() || [-23.5505, -46.6333]
  }

  selectedWarehouseCoordinates() {
    const option = this.warehouseTarget.selectedOptions[0]
    return this.validCoordinates(option?.dataset.latitude, option?.dataset.longitude)
  }

  defaultWarehouseCoordinates() {
    if (!this.hasDefaultLatitudeValue || !this.hasDefaultLongitudeValue) return null
    return this.validCoordinates(this.defaultLatitudeValue, this.defaultLongitudeValue)
  }

  validCoordinates(latitudeValue, longitudeValue) {
    const latitude = Number(latitudeValue)
    const longitude = Number(longitudeValue)
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude) || latitude === 0 || longitude === 0) return null
    return [latitude, longitude]
  }

  undo() {
    this.points.pop()
    this.render()
  }

  clear() {
    this.points = []
    this.render()
  }

  addPoint(latlng) {
    this.points.push([latlng.lat, latlng.lng])
    this.render()
  }

  render() {
    this.markers.forEach((marker) => marker.remove())
    this.markers = this.points.map((point, index) => this.createVertex(point, index))
    this.polygon.setLatLngs(this.points)
    this.syncGeojson()

    const count = this.points.length
    this.counterTarget.textContent = `${count} ${count === 1 ? "ponto" : "pontos"}`
    this.statusTarget.classList.remove("text-danger", "text-success")

    if (count >= 3) {
      this.mapTarget.classList.remove("border", "border-danger")
      this.statusTarget.textContent = "Região pronta. Arraste os pontos para ajustar."
      this.statusTarget.classList.add("text-success")
    } else {
      this.statusTarget.textContent = `Adicione mais ${3 - count} ${3 - count === 1 ? "ponto" : "pontos"}.`
    }
  }

  createVertex(point, index) {
    const icon = L.divIcon({
      className: "restriction-map-vertex",
      html: "<span></span>",
      iconSize: [18, 18],
      iconAnchor: [9, 9]
    })
    const marker = L.marker(point, { draggable: true, icon }).addTo(this.map)

    marker.on("drag", (event) => {
      const position = event.target.getLatLng()
      this.points[index] = [position.lat, position.lng]
      this.polygon.setLatLngs(this.points)
      this.syncGeojson()
    })
    marker.on("click", (event) => L.DomEvent.stopPropagation(event))
    return marker
  }

  syncGeojson() {
    if (this.points.length < 3) {
      this.geojsonTarget.value = ""
      return
    }

    const coordinates = this.points.map(([latitude, longitude]) => [longitude, latitude])
    coordinates.push([...coordinates[0]])
    this.geojsonTarget.value = JSON.stringify({ type: "Polygon", coordinates: [coordinates] })
  }

  readPoints() {
    try {
      const geometry = JSON.parse(this.geojsonTarget.value || "{}")
      const ring = geometry.type === "MultiPolygon" ? geometry.coordinates?.[0]?.[0] : geometry.coordinates?.[0]
      if (!Array.isArray(ring)) return []

      const coordinates = ring.map(([longitude, latitude]) => [Number(latitude), Number(longitude)])
      if (coordinates.length > 1 && this.samePoint(coordinates[0], coordinates[coordinates.length - 1])) coordinates.pop()
      return coordinates.filter(([latitude, longitude]) => Number.isFinite(latitude) && Number.isFinite(longitude))
    } catch (_error) {
      return []
    }
  }

  samePoint(first, second) {
    return first[0] === second[0] && first[1] === second[1]
  }

  showValidationToast(message) {
    const container = document.createElement("div")
    container.className = "toast-container position-fixed top-0 end-0 p-3"
    container.style.zIndex = "1080"
    container.innerHTML = `<div class="toast align-items-center text-bg-danger border-0" role="alert"><div class="d-flex"><div class="toast-body"></div><button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast" aria-label="Fechar"></button></div></div>`
    container.querySelector(".toast-body").textContent = message
    document.body.appendChild(container)
    const toastElement = container.querySelector(".toast")
    toastElement.addEventListener("hidden.bs.toast", () => container.remove(), { once: true })
    Toast.getOrCreateInstance(toastElement, { delay: 4000 }).show()
  }
}
